/**
 * Navigation helper utilities for the golf application
 * Provides common functions for navigation state management and responsive behavior
 */

import { useCallback, useEffect, useState, useMemo, RefObject } from 'react'

interface ScreenSize {
  isMobile: boolean
  isTablet: boolean
  isTabletPortrait: boolean
  isTabletLandscape: boolean
  isDesktop: boolean
  width: number
  height: number
  orientation: 'portrait' | 'landscape'
}

interface NavigationStateStorage {
  lastVisitedPath: string | null
  menuPreferences: Record<string, unknown>
  navigationHistory: string[]
}

interface NavigationStateStorageReturn {
  state: NavigationStateStorage
  updateLastVisited: (path: string) => void
  updateMenuPreference: (preference: string, value: unknown) => void
  clearState: () => void
}

interface KeyboardNavigationOptions {
  onEscape?: () => void
  onEnter?: () => void
  onArrowUp?: () => void
  onArrowDown?: () => void
  enabled?: boolean
}

interface SwipeOptions {
  minSwipeDistance?: number
  maxSwipeTime?: number
  preventVerticalSwipe?: boolean
  velocityThreshold?: number
}

interface SwipeHandlers {
  onTouchStart: (e: React.TouchEvent<HTMLElement>) => void
  onTouchMove: (e: React.TouchEvent<HTMLElement>) => void
  onTouchEnd: () => void
  isSwiping: boolean
}

const getScreenSize = (): ScreenSize => {
  const width = window.innerWidth
  const height = window.innerHeight
  const orientation: 'portrait' | 'landscape' = height > width ? 'portrait' : 'landscape'
  const isTablet = width >= 768 && width < 1024

  return {
    isMobile: width < 768,
    isTablet,
    isTabletPortrait: isTablet && orientation === 'portrait',
    isTabletLandscape: isTablet && orientation === 'landscape',
    isDesktop: width >= 1024,
    width,
    height,
    orientation
  }
}

export const useResponsiveNavigation = (): ScreenSize => {
  const [screenSize, setScreenSize] = useState<ScreenSize>(getScreenSize)

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null

    const handleChange = () => {
      clearTimeout(timeoutId!)
      timeoutId = setTimeout(() => {
        setScreenSize(getScreenSize())
      }, 150)
    }

    window.addEventListener('resize', handleChange)
    // Also listen for orientation changes
    window.addEventListener('orientationchange', handleChange)

    return () => {
      clearTimeout(timeoutId!)
      window.removeEventListener('resize', handleChange)
      window.removeEventListener('orientationchange', handleChange)
    }
  }, [])

  return screenSize
}

export const isPathActive = (path: string, currentPath: string, exact: boolean = false): boolean => {
  if (exact) {
    return currentPath === path
  }

  if (path === '/dashboard') {
    return currentPath === '/dashboard'
  }

  return currentPath.startsWith(path)
}

export const useNavigationSwipe = (
  onSwipeRight?: () => void,
  onSwipeLeft?: () => void,
  options: SwipeOptions = {}
): SwipeHandlers => {
  const [touchStart, setTouchStart] = useState<number | null>(null)
  const [touchEnd, setTouchEnd] = useState<number | null>(null)
  const [touchStartTime, setTouchStartTime] = useState<number | null>(null)
  const [isSwiping, setIsSwiping] = useState(false)

  const config = useMemo(
    () => ({
      minSwipeDistance: 75,
      maxSwipeTime: 500,
      preventVerticalSwipe: true,
      velocityThreshold: 0.3,
      ...options
    }),
    [options]
  )

  const onTouchStart = useCallback((e: React.TouchEvent<HTMLElement>) => {
    setTouchEnd(null)
    setTouchStart(e.targetTouches[0].clientX)
    setTouchStartTime(Date.now())
    setIsSwiping(false)
  }, [])

  const onTouchMove = useCallback(
    (e: React.TouchEvent<HTMLElement>) => {
      if (!touchStart) return

      const currentTouch = e.targetTouches[0].clientX
      setTouchEnd(currentTouch)

      const distance = Math.abs(touchStart - currentTouch)
      if (distance > 10) {
        setIsSwiping(true)

        if (config.preventVerticalSwipe) {
          e.preventDefault()
        }
      }
    },
    [touchStart, config]
  )

  const onTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd || !touchStartTime) return

    const distance = touchStart - touchEnd
    const timeTaken = Date.now() - touchStartTime
    const velocity = Math.abs(distance) / timeTaken

    const isValidSwipe =
      Math.abs(distance) > config.minSwipeDistance ||
      (velocity > config.velocityThreshold && Math.abs(distance) > 30)

    const isQuickSwipe = timeTaken < config.maxSwipeTime

    if (isValidSwipe && isQuickSwipe) {
      const isLeftSwipe = distance > 0
      const isRightSwipe = distance < 0

      if (isRightSwipe && onSwipeRight) {
        if (window.navigator.vibrate) {
          window.navigator.vibrate(10)
        }
        onSwipeRight()
      }

      if (isLeftSwipe && onSwipeLeft) {
        if (window.navigator.vibrate) {
          window.navigator.vibrate(10)
        }
        onSwipeLeft()
      }
    }

    setTouchStart(null)
    setTouchEnd(null)
    setTouchStartTime(null)
    setIsSwiping(false)
  }, [touchStart, touchEnd, touchStartTime, onSwipeRight, onSwipeLeft, config])

  return {
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    isSwiping
  }
}

export const useNavigationStateStorage = (key: string = 'navigation-state'): NavigationStateStorageReturn => {
  const [state, setState] = useState<NavigationStateStorage>(() => {
    try {
      const savedState = localStorage.getItem(key)
      return savedState
        ? JSON.parse(savedState)
        : {
            lastVisitedPath: null,
            menuPreferences: {
              preferredView: 'default',
              lastOpenTime: null
            },
            navigationHistory: []
          }
    } catch (error) {
      console.debug('Failed to load navigation state:', error)
      return {
        lastVisitedPath: null,
        menuPreferences: {},
        navigationHistory: []
      }
    }
  })

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(state))
    } catch (error) {
      console.debug('Failed to save navigation state:', error)
    }
  }, [key, state])

  const updateLastVisited = useCallback((path: string) => {
    setState(prev => ({
      ...prev,
      lastVisitedPath: path,
      navigationHistory: [path, ...prev.navigationHistory.filter(p => p !== path).slice(0, 4)]
    }))
  }, [])

  const updateMenuPreference = useCallback((preference: string, value: unknown) => {
    setState(prev => ({
      ...prev,
      menuPreferences: {
        ...prev.menuPreferences,
        [preference]: value
      }
    }))
  }, [])

  return {
    state,
    updateLastVisited,
    updateMenuPreference,
    clearState: () =>
      setState({
        lastVisitedPath: null,
        menuPreferences: {},
        navigationHistory: []
      })
  }
}

export const useFocusTrap = (isOpen: boolean, containerRef: RefObject<HTMLDivElement | null>): void => {
  useEffect(() => {
    if (!isOpen || !containerRef?.current) return

    const container = containerRef.current
    if (!container || typeof container.querySelectorAll !== 'function') return

    try {
      const focusableElements = container.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
      )

      if (focusableElements.length === 0) return

      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]

      const previouslyFocused = document.activeElement as HTMLElement | null

      setTimeout(() => {
        firstElement?.focus()
      }, 100)

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key !== 'Tab') return

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault()
            lastElement.focus()
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault()
            firstElement.focus()
          }
        }
      }

      container.addEventListener('keydown', handleKeyDown)

      return () => {
        container.removeEventListener('keydown', handleKeyDown)
        if (previouslyFocused && previouslyFocused.focus) {
          previouslyFocused.focus()
        }
      }
    } catch (error) {
      console.warn('Focus trap error:', error)
      return () => {} // Return empty cleanup function
    }
  }, [isOpen, containerRef])
}

export const useKeyboardNavigation = (options: KeyboardNavigationOptions = {}): void => {
  const { onEscape, onEnter, onArrowUp, onArrowDown, enabled = true } = options

  useEffect(() => {
    if (!enabled) return

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          if (onEscape) {
            e.preventDefault()
            onEscape()
          }
          break
        case 'Enter':
          if (onEnter && (e.target as HTMLElement).tagName === 'A') {
            onEnter()
          }
          break
        case 'ArrowUp':
          if (onArrowUp) {
            e.preventDefault()
            onArrowUp()
          }
          break
        case 'ArrowDown':
          if (onArrowDown) {
            e.preventDefault()
            onArrowDown()
          }
          break
        default:
          break
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [enabled, onEscape, onEnter, onArrowUp, onArrowDown])
}

export const announceToScreenReader = (message: string, priority: 'polite' | 'assertive' = 'polite'): void => {
  const announcement = document.createElement('div')
  announcement.setAttribute('aria-live', priority)
  announcement.setAttribute('aria-atomic', 'true')
  announcement.setAttribute('class', 'visually-hidden')
  announcement.textContent = message

  document.body.appendChild(announcement)

  setTimeout(() => {
    document.body.removeChild(announcement)
  }, 1000)
}
